// src/hooks/usePushNotification.ts
/**
 * 푸시 알림 구독 관리 훅 — 웹은 Web Push, 네이티브 앱은 FCM으로 자동 분기
 *
 * 사용법:
 *   const { isSupported, isSubscribed, isLoading, subscribe, unsubscribe } = usePushNotification();
 *
 * 웹 흐름:
 *   1. 브라우저 지원 여부 확인 (isSupported)
 *   2. 기존 구독 상태 로드 (isSubscribed)
 *   3. subscribe() → 알림 권한 요청 → Push 구독 → 백엔드 등록 (/reports/push/subscribe/)
 *   4. unsubscribe() → Push 구독 해제 → 백엔드 삭제
 *
 * 네이티브(Capacitor) 흐름:
 *   1. isSupported는 항상 true (플랫폼이 FCM 지원)
 *   2. 앱 시작 시 권한이 이미 허용돼 있으면 조용히 register() 재호출해 토큰 갱신(로테이션 대응)
 *   3. subscribe() → 권한 요청 → register() → 'registration' 이벤트로 토큰 수신 → 백엔드 등록 (/reports/fcm/register/)
 *   4. unsubscribe() → 백엔드 삭제 → unregister()
 *   구독 상태는 기기별로 로컬스토리지(`fcm_token`)에 저장해 재구독 여부를 판단한다
 *   (FCM은 Web Push와 달리 "현재 구독 조회" API가 없음).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import api, { getAccessToken } from '../api/axiosInstance';
import { API_ENDPOINTS } from '../constants/apiEndpoints';

const FCM_TOKEN_KEY = 'fcm_token';

/** base64url 문자열을 Uint8Array로 변환 (applicationServerKey 형식) */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

interface UsePushNotificationResult {
  isSupported: boolean;
  isSubscribed: boolean;
  /** 브라우저에서 알림을 명시적으로 차단한 상태 */
  isBlocked: boolean;
  isLoading: boolean;
  error: string | null;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
}

export function usePushNotification(): UsePushNotificationResult {
  const isNative = useRef(Capacitor.isNativePlatform()).current;

  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── 네이티브(FCM) 초기화 ────────────────────────────────────────────────
  useEffect(() => {
    if (!isNative) return;
    setIsSupported(true);

    let registrationListener: { remove: () => void } | undefined;
    let errorListener: { remove: () => void } | undefined;

    (async () => {
      const status = await PushNotifications.checkPermissions();
      if (status.receive === 'denied') {
        setIsBlocked(true);
        return;
      }

      const savedToken = localStorage.getItem(FCM_TOKEN_KEY);
      setIsSubscribed(!!savedToken && status.receive === 'granted');

      // ⚠️ 로그인 전에는 등록하지 않는다.
      // /reports/fcm/register/는 인증이 필요한데, 미로그인 상태에서 호출하면 401이 나고
      // axios 401 인터셉터가 refresh 토큰이 없다고 판단해 window.location.href='/login'로
      // 전체 페이지를 다시 띄운다. 그러면 앱이 재시작되며 다시 등록을 시도해 무한 리로드에
      // 빠진다(에뮬레이터 검증에서 실제로 재현됨). 로그인 후에는 subscribe()가 등록을 맡는다.
      // 권한이 이미 허용돼 있으면 재프롬프트 없이 토큰만 조용히 갱신(로테이션 대응)
      if (status.receive === 'granted' && getAccessToken()) {
        registrationListener = await PushNotifications.addListener('registration', async (token) => {
          if (token.value === savedToken) return; // 변경 없음
          try {
            await api.post(API_ENDPOINTS.FCM.REGISTER, { token: token.value, platform: 'android' });
            localStorage.setItem(FCM_TOKEN_KEY, token.value);
            setIsSubscribed(true);
          } catch (err) {
            console.error('[FCM] 토큰 갱신 등록 실패:', err);
          }
        });
        errorListener = await PushNotifications.addListener('registrationError', (err) => {
          console.error('[FCM] 등록 실패:', err.error);
        });
        await PushNotifications.register();
      }
    })();

    return () => {
      registrationListener?.remove();
      errorListener?.remove();
    };
  }, [isNative]);

  // ── 웹(Web Push) 초기화 ─────────────────────────────────────────────────
  useEffect(() => {
    if (isNative) return;

    const supported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;

    setIsSupported(supported);
    if (!supported) return;

    // 브라우저 차단 여부 즉시 반영
    if (Notification.permission === 'denied') {
      setIsBlocked(true);
      return;
    }

    navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((subscription) => {
        setIsSubscribed(subscription !== null);
      })
      .catch(() => {
        setIsSubscribed(false);
      });
  }, [isNative]);

  const subscribeNative = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const status = await PushNotifications.requestPermissions();
      if (status.receive === 'denied') {
        setIsBlocked(true);
        setError('기기 설정에서 알림이 차단되어 있습니다. 설정 → 앱 → 알림에서 허용으로 변경해 주세요.');
        return;
      }
      if (status.receive !== 'granted') {
        setError('알림 권한이 필요합니다. 다시 시도해 주세요.');
        return;
      }

      const token = await new Promise<string>(async (resolve, reject) => {
        // register() 호출 전에 리스너부터 붙여야 이벤트를 놓치지 않는다
        const regHandle = await PushNotifications.addListener('registration', (t) => {
          regHandle.remove();
          errHandle.remove();
          resolve(t.value);
        });
        const errHandle = await PushNotifications.addListener('registrationError', (err) => {
          regHandle.remove();
          errHandle.remove();
          reject(new Error(err.error));
        });
        await PushNotifications.register();
      });

      await api.post(API_ENDPOINTS.FCM.REGISTER, { token, platform: 'android' });
      localStorage.setItem(FCM_TOKEN_KEY, token);
      setIsSubscribed(true);
    } catch (err: any) {
      console.error('[FCM] 구독 실패:', err);
      setError('알림 구독 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const unsubscribeNative = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem(FCM_TOKEN_KEY);
      if (token) {
        try {
          await api.delete(API_ENDPOINTS.FCM.REGISTER, { data: { token } });
        } catch (backendErr: any) {
          if (backendErr?.response?.status !== 404) throw backendErr;
        }
      }
      await PushNotifications.unregister();
      localStorage.removeItem(FCM_TOKEN_KEY);
      setIsSubscribed(false);
    } catch (err: any) {
      console.error('[FCM] 구독 해제 실패:', err);
      setError('알림 해제 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const subscribeWeb = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. 알림 권한 요청
      const permission = await Notification.requestPermission();
      if (permission === 'denied') {
        setIsBlocked(true);
        setError('브라우저에서 알림이 차단되어 있습니다. 주소창 왼쪽 자물쇠 아이콘 → 알림 → 허용으로 변경해 주세요.');
        return;
      }
      if (permission !== 'granted') {
        setError('알림 권한이 필요합니다. 다시 시도해 주세요.');
        return;
      }

      // 2. 백엔드에서 VAPID 공개키 가져오기
      const { data: keyData } = await api.get(API_ENDPOINTS.PUSH.VAPID_PUBLIC_KEY);
      const publicKey: string = keyData.public_key;

      // 3. Service Worker가 준비될 때까지 대기
      const registration = await navigator.serviceWorker.ready;

      // 4. 기존 구독이 있으면 제거 후 재구독 (VAPID 키 불일치 방지)
      const existing = await registration.pushManager.getSubscription();
      if (existing) await existing.unsubscribe();

      // 5. Push 구독 생성
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      // 6. 구독 정보를 백엔드에 저장
      const subJson = subscription.toJSON();
      await api.post(API_ENDPOINTS.PUSH.SUBSCRIBE, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });

      setIsSubscribed(true);
    } catch (err: any) {
      console.error('[Push] 구독 실패:', err);
      if (err?.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('알림 구독 중 오류가 발생했습니다.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const unsubscribeWeb = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        setIsSubscribed(false);
        return;
      }

      // 1. 백엔드에서 구독 삭제 (404는 무시 — 다른 계정으로 등록된 구독일 수 있음)
      try {
        await api.delete(API_ENDPOINTS.PUSH.SUBSCRIBE, {
          data: { endpoint: subscription.endpoint },
        });
      } catch (backendErr: any) {
        if (backendErr?.response?.status !== 404) throw backendErr;
      }

      // 2. 브라우저 구독 해제 (백엔드 결과와 무관하게 항상 실행)
      await subscription.unsubscribe();

      setIsSubscribed(false);
    } catch (err: any) {
      console.error('[Push] 구독 해제 실패:', err);
      setError('알림 해제 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const subscribe = isNative ? subscribeNative : subscribeWeb;
  const unsubscribe = isNative ? unsubscribeNative : unsubscribeWeb;

  return { isSupported, isSubscribed, isBlocked, isLoading, error, subscribe, unsubscribe };
}
